import { FetchService } from 'dc-services/fetch.service';
import { TextconstantService } from 'dc-services/textconstant.service';
import { generateNewModal, showModal, closeModal } from '../../node_modules/cronus-theme/default/global/js/modal.js';

/**
 * "Preis auf Anfrage": oeffnet ein Anfrage-Modal fuer Artikel ohne Preis
 * (Itemcard und Artikellisten, ausgeloest ueber .js-itemRequest-Trigger).
 * Sendet die Anfrage an /em/itemRequest/send (siehe backend/itemRequest).
 */

const ENDPOINT = '/em/itemRequest/send';

const TC = {
    title:           'item_request_title',
    itemNoLabel:     'item_request_item_no_label',
    variantLabel:    'item_request_variant_label',
    nameLabel:       'item_request_name_label',
    emailLabel:      'item_request_email_label',
    customerNoLabel: 'item_request_customer_no_label',
    quantityLabel:   'item_request_quantity_label',
    commentLabel:    'item_request_comment_label',
    privacyLabel:    'item_request_privacy_label',
    submit:          'item_request_submit',
    successTitle:    'item_request_success_title',
    successBody:     'item_request_success_body',
    errorTitle:      'item_request_error_title',
    errorBody:       'item_request_error_body',
};

function isEmptyOrZeroPrice(priceEl) {
    const text = priceEl.textContent.replace(/\s+/g, ' ').trim();
    if (text === '') return true;

    const match = text.match(/(\d{1,3}(?:\.\d{3})*|\d+)(,\d+)?/);
    if (!match) return true;

    const normalized = match[0].replace(/\./g, '').replace(',', '.');
    return parseFloat(normalized) === 0;
}

/**
 * Liest die Varianten ohne Preis aus der (opaken) Varianten-Tabelle der Itemcard
 * (#itemcardVariants, siehe itemRequestVariantTable.js) aus - es gibt dafuer keine
 * strukturierten Mustache-Felder, nur bereits gerendertes HTML. Katalog zeigt nie
 * Preise, daher landen dort automatisch alle Varianten im Dropdown; b2b bekommt so
 * automatisch nur die Varianten ohne eigenen Preis.
 *
 * Je nach Theme wird eine Variantenzeile ueber unterschiedliche Core-Partials
 * gerendert: b2b nutzt itemlistElementIsVariant (Klasse --isVariant), der Katalog
 * dagegen itemlistElementNoVariant (Klasse --noVariant, kein .itemlistElement__prices
 * -Element ueberhaupt vorhanden, da der Katalog nie Preise zeigt) - beide werden
 * daher beruecksichtigt. Fehlt das Preis-Element komplett, gilt das ebenfalls als
 * "kein Preis" statt als Grund zum Ausschliessen.
 */
function getVariantOptions() {
    const container = document.querySelector('#itemcardVariants');
    if (!container) return [];

    const options = [];
    container.querySelectorAll('.itemlistElement__wrapper--isVariant, .itemlistElement__wrapper--noVariant').forEach((row) => {
        const priceEl = row.querySelector('.itemlistElement__prices');
        const hasRealPrice = priceEl && !isEmptyOrZeroPrice(priceEl);
        if (hasRealPrice) return;

        const media = row.querySelector('[id^="item_"]');
        const itemNo = media ? media.id.replace(/^item_/, '') : (row.querySelector('.itemlistElement__item_no')?.textContent.trim() || '');
        const description = row.querySelector('.itemlistElement__name')?.textContent.replace(/\s+/g, ' ').trim() || '';
        if (itemNo) options.push({ itemNo, description });
    });
    return options;
}

function getDebtorPrefill() {
    // Kundennr./Name kommen - falls vorhanden - aus data-Attributen am <body>
    // (vom Core-Header gesetzt). Fallback: leer, Nutzer traegt selbst ein.
    const body = document.body;
    return {
        customerNo: body.dataset.customerNo || '',
        name: body.dataset.customerName || '',
    };
}

async function getText(key) {
    // window.GLOBALS.tc (siehe pickel_global/js_globals.php) liefert feste deutsche
    // Fallbacks synchron, ohne dass der tc-Key im Backend/CMS angelegt sein muss.
    // Sobald der Key dort gepflegt wird, liefert TextconstantService den echten Text.
    const fromGlobals = window.GLOBALS?.tc?.[key];
    if (fromGlobals) return fromGlobals;
    return TextconstantService.getTextConstant(key);
}

async function getTexts(keys) {
    const values = await Promise.all(keys.map(getText));
    return Object.fromEntries(keys.map((k, i) => [k, values[i]]));
}

async function showFeedbackModal(titleKey, bodyKey) {
    const modal = generateNewModal('modalTemplate');
    if (!modal) return;

    modal.classList.remove('modal--loading', 'modal--lg', 'modal--md');
    modal.classList.add('modal--sm');

    const t = await getTexts([titleKey, bodyKey]);
    const titleEl = modal.querySelector('.modal__title');
    const bodyEl = modal.querySelector('.modal__body');
    if (titleEl) titleEl.textContent = t[titleKey];
    if (bodyEl) bodyEl.innerHTML = `<p>${t[bodyKey]}</p>`;

    showModal(modal);
}

function getMainItemImage() {
    // Fallback fuer den Fall, dass item.imagelink im Mustache-Kontext leer ist - zeigt
    // das (Mutter-)Artikelbild der aktuell angezeigten Artikelkarte, unabhaengig davon,
    // welche Variante im Dropdown ausgewaehlt ist.
    return document.querySelector('.itemcardMedia img')?.getAttribute('src') || '';
}

async function showItemRequestModal(trigger) {
    const itemNo = trigger.dataset.itemNo || '';
    const itemDescription = trigger.dataset.itemDescription || '';
    const itemImage = trigger.dataset.itemImage || getMainItemImage();
    const debtor = getDebtorPrefill();
    const variantOptions = getVariantOptions();

    const modal = generateNewModal('modalTemplate');
    if (!modal) return;

    modal.classList.remove('modal--loading', 'modal--sm', 'modal--lg');
    modal.classList.add('modal--md');

    const t = await getTexts(Object.values(TC));

    const titleEl = modal.querySelector('.modal__title');
    const bodyEl = modal.querySelector('.modal__body');
    if (titleEl) titleEl.textContent = t[TC.title];

    const variantSelectHtml = variantOptions.length > 0
        ? `
            <div class="marginBottom--small">
                <label for="itemRequest_variant">${t[TC.variantLabel]}</label>
                <div class="inputGroup">
                    <div class="selectBody">
                        <select id="itemRequest_variant" name="variant">
                            ${variantOptions.map((option) => `
                                <option value="${option.itemNo}" ${option.itemNo === itemNo ? 'selected' : ''}>
                                    ${option.itemNo}${option.description ? ' - ' + option.description : ''}
                                </option>
                            `).join('')}
                        </select>
                    </div>
                </div>
            </div>
        `
        : '';

    if (bodyEl) {
        bodyEl.innerHTML = `
            <div class="itemRequestModal">
                <div class="itemRequestModal__media">
                    ${itemImage ? `<img src="${itemImage}" alt="${itemDescription}">` : ''}
                    <div><strong>${itemDescription}</strong></div>
                    <div class="itemRequestModal__itemNo">${t[TC.itemNoLabel]}: ${itemNo}</div>
                </div>
                <form class="itemRequestForm">
                    ${variantSelectHtml}
                    <div class="row gridGap--small">
                        <div class="col-md-6 marginBottom--small">
                            <label for="itemRequest_name">${t[TC.nameLabel]} *</label>
                            <div class="inputGroup">
                                <input type="text" id="itemRequest_name" name="name" value="${debtor.name}" required>
                            </div>
                        </div>
                        <div class="col-md-6 marginBottom--small">
                            <label for="itemRequest_email">${t[TC.emailLabel]} *</label>
                            <div class="inputGroup">
                                <input type="email" id="itemRequest_email" name="email" required>
                            </div>
                        </div>
                    </div>
                    <div class="row gridGap--small">
                        <div class="col-md-6 marginBottom--small">
                            <label for="itemRequest_customerNo">${t[TC.customerNoLabel]}</label>
                            <div class="inputGroup">
                                <input type="text" id="itemRequest_customerNo" name="customerNo" value="${debtor.customerNo}">
                            </div>
                        </div>
                        <div class="col-md-6 marginBottom--small">
                            <label for="itemRequest_quantity">${t[TC.quantityLabel]}</label>
                            <div class="inputGroup">
                                <input type="number" id="itemRequest_quantity" name="quantity" min="1" step="1" value="1" required>
                            </div>
                        </div>
                    </div>
                    <div class="marginBottom">
                        <label for="itemRequest_comment">${t[TC.commentLabel]}</label>
                        <div class="inputGroup">
                            <textarea id="itemRequest_comment" name="comment" rows="3"></textarea>
                        </div>
                    </div>
                    <div class="marginBottom">
                        <label class="specialcheckbox" for="itemRequest_privacy">
                            <input type="checkbox" name="privacy" id="itemRequest_privacy" required value="on">
                            <i></i>
                            <span aria-hidden="true">${t[TC.privacyLabel]} <span aria-hidden="true">*</span></span>
                        </label>
                    </div>
                    <button type="submit" class="button button--primary js-itemRequestSubmit">${t[TC.submit]}</button>
                </form>
            </div>
        `;
    }

    showModal(modal);

    const form = modal.querySelector('.itemRequestForm');
    form?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('.js-itemRequestSubmit');
        btn.disabled = true;

        const variantSelect = form.querySelector('#itemRequest_variant');
        const selectedVariant = variantSelect
            ? variantOptions.find((option) => option.itemNo === variantSelect.value)
            : null;

        const payload = {
            item_no: selectedVariant ? selectedVariant.itemNo : itemNo,
            item_description: selectedVariant ? selectedVariant.description : itemDescription,
            quantity: form.querySelector('#itemRequest_quantity').value,
            name: form.querySelector('#itemRequest_name').value,
            email: form.querySelector('#itemRequest_email').value,
            customer_no: form.querySelector('#itemRequest_customerNo').value,
            comment: form.querySelector('#itemRequest_comment').value,
        };

        try {
            const response = await FetchService.post(ENDPOINT, payload);
            closeModal(modal);
            if (response && response.success) {
                await showFeedbackModal(TC.successTitle, TC.successBody);
            } else {
                await showFeedbackModal(TC.errorTitle, TC.errorBody);
            }
        } catch (err) {
            console.error('Item request error:', err);
            closeModal(modal);
            await showFeedbackModal(TC.errorTitle, TC.errorBody);
        }
    });
}

/**
 * Barrierefreiheit (WCAG 4.1.2): Anfrage-Trigger sind teils reine Icon-Buttons - im
 * serverseitigen Mustache-Kontext ist tc.item_request leer, daher Name hier aus
 * GLOBALS.tc nachziehen, wenn der Button weder Text noch aria-label hat.
 */
function labelItemRequestTriggers() {
    const label = window.GLOBALS?.tc?.item_request;
    if (!label) return;

    document.querySelectorAll('.js-itemRequest').forEach((trigger) => {
        if (trigger.textContent.trim() === '' && !trigger.getAttribute('aria-label')) {
            trigger.setAttribute('aria-label', label);
        }
    });
}

document.addEventListener('DOMContentLoaded', labelItemRequestTriggers);

document.addEventListener('click', (e) => {
    const trigger = e.target.closest('.js-itemRequest');
    if (trigger) {
        e.preventDefault();
        showItemRequestModal(trigger);
    }
});
